import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { EMPTY } from 'rxjs';
import { SeoService } from '../seo/seo.service';

@Injectable({
  providedIn: 'root',
})
export class BannerService {
  url = environment.urlApi;
  isBrowser: boolean;

  constructor(
    @Inject(PLATFORM_ID) platformId: object,
    private http: HttpClient,
    private seo: SeoService
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  /**
   * @description
   * Retorna lista de banners
   * @param type
   * @param bothPlatforms { boolean } Se a requisição será feita tanto no servidor quanto no browser. Default false.
   */
  index(
    type:
      | 'home_header'
      | 'home_highlight'
      | 'home_institutional'
      | 'category'
      | 'onboarding'
      | 'lp_header'
      | 'lp_highlight'
      | 'lp_institutional'
      | 'lp_list'
      | 'lp_simple'
      | 'lp_image'
      | 'exclusive',
    bothPlatforms?: boolean,
    per_page?: number,
    with_exclusives?: boolean
  ) {
    if (!this.isBrowser && !bothPlatforms) {
      return EMPTY;
    }

    return this.http.get(`${this.url}/banners`, {
      params: {
        type,
        with_exclusives: with_exclusives ? "1" : '0',
        per_page: per_page ? per_page.toString() : '',
      },
    });
  }

  /**
   * @description
   * Retorna banners a partir de uma página.
   *
   * Realiza uma requisição para obter o ID do menu e por fim
   * obter os banners.
   *
   * @param path { string }
   * @param type { string }
   * @param area { string } Página ou Categoria
   */
  async indexByPage(
    path: string,
    type:
      | 'menu_header'
      | 'category_header'
      | 'subcategory_header'
      | 'menu_content'
      | 'content_institutional',
    area: string = 'menu',
    per_page?: number,
    bothPlatforms?: boolean
  ): Promise<any[]> {
    return new Promise(async (resolve, reject) => {
      try {
        // if (!bothPlatforms) {
        //   return EMPTY;
        // }

        let res: any = await this.http
          .get(`${this.url}/menu`, { params: { path } })
          .toPromise();

        this.seo.update(res.data);

        if (!this.isBrowser) {
          return resolve([]);
        }

        const { id } = res.data;
        res = await this.http
          .get(`${this.url}/banners`, {
            params: {
              area,
              id,
              type,
              per_page: per_page ? per_page.toString() : '',
            },
          })
          .toPromise();

        const { data } = res;
        resolve(data);
      } catch (e) {
        reject(e);
      }
    });
  }

  /**
   * @param slug { string } Slug da categoria
   * @param type { string }
   * @param area { string }
   */
  async indexByCategory(
    slug: string,
    type: 'category_header' | 'subcategory_header',
    area: 'category' = 'category',
    per_page?: number,
    bothPlatforms?: boolean
  ): Promise<any[]> {
    return new Promise(async (resolve, reject) => {
      try {
        if (!bothPlatforms) {
          return EMPTY;
        }

        let res: any = await this.http
          .get(`${this.url}/category/${slug}`)
          .toPromise();

        const { description: head, name: title, images } = res.data;
        const seoParams = { head, title, images };

        this.seo.update(seoParams);

        if (!this.isBrowser) {
          return resolve([]);
        }

        const { id } = res.data;
        res = await this.http
          .get(`${this.url}/banners`, {
            params: {
              area,
              id,
              type,
              per_page: per_page ? per_page.toString() : '',
            },
          })
          .toPromise();

        const { data } = res;
        resolve(data);
      } catch (e) {
        reject(e);
      }
    });
  }

  /**
   * @description
   * Retorna banners a partir de uma LP.
   *
   * Realiza uma requisição para obter o ID do menu e por fim
   * obter os banners.
   *
   * @param id { number }
   * @param type { string }
   * @param bothPlatforms { boolean } Se a requisição será feita tanto no servidor quanto no browser. Default false.
   */
  indexByLanding(
    id: number,
    type:
      | 'lp_header'
      | 'lp_highlight'
      | 'lp_institutional'
      | 'lp_list'
      | 'lp_simple'
      | 'lp_image',
    per_page?: number,
    bothPlatforms?: boolean
  ) {
    if (!this.isBrowser && !bothPlatforms) {
      return EMPTY;
    }

    return this.http.get(`${this.url}/banners`, {
      params: {
        area: 'lp',
        id: id.toString(),
        type,
        per_page: per_page ? per_page.toString() : '',
      },
    });
  }

  /**
   * @description
   * Retorna lista de banners exclusivos

   * @param bothPlatforms { boolean } Se a requisição será feita tanto no servidor quanto no browser. Default false.
   */
  getExclusivesBanners(
    bothPlatforms?: boolean,
    per_page?: number
  ) {
    if (!this.isBrowser && !bothPlatforms) {
      return EMPTY;
    }

    return this.http.get(`${this.url}/banners/exclusives`, {
      params: {
        per_page: per_page ? per_page.toString() : '',
      },
    });
  }
}
